import React, { useEffect, useState } from 'react';
import SortBy, {
  SortByItemType,
} from '@/pages/intelligence/components/TableComponents/components/SortBy';
import TableShowData from '@/pages/intelligence/components/TableComponents/components/TableShowData';
import {
  valueFormat,
  growthFormat,
} from '@/pages/intelligence/utils/commonUtils';
import CloumsCenter from '@/pages/intelligence/components/TableComponents/components/CloumsCenter';
import AppAnnie from '@/pages/intelligence/assets/svg/dataSource/app-annie.svg';
import { useIntl } from 'umi';
import SensorTowerIcon from '@/pages/intelligence/assets/svg/dataSource/sensor-tower.svg';
import {
  DataSourceBubbleInfo,
  DATASOURCE_NAME,
} from '@/pages/intelligence/components/DataSourceComponents';
import { getDataSourceIcon } from '@/pages/intelligence/utils/getDataSourceImg';
import { buildMetricCompareData } from './metricCompare';

interface ColumnType {
  key: string;
  title: string;
  dataSource: string;
  currentSortKey: string;
  currentSortValue: '' | 'desc' | 'asc';
  onSortChage: (e: SortByItemType) => void;
  sortMetric: string;
  textContent: React.ReactNode;
  isNoDropdown?: boolean;
  svgIcon?: any;
  metricKey?: string;
  entityType?: string;
  dateType?: string;
  startDate?: string;
  endDate?: string;
  gameName?: string;
  gameCover?: string;
  sourceDataOfDom?: any;
  /** 国际化 key，存在时用 formatMessage 渲染 title */
  titleKey?: string;
}

export const RetentionMetricColumn = (params: ColumnType) => {
  const {
    key,
    title,
    sortMetric,
    dataSource,
    currentSortKey,
    currentSortValue,
    textContent,
    onSortChage,
    metricKey,
    entityType = '',
    dateType,
    startDate,
    endDate,
    svgIcon,
    titleKey,
  } = params;
  const { formatMessage } = useIntl();
  const displayTitle = titleKey ? formatMessage({ id: titleKey }) : title;
  const headerSourceIcon =
    svgIcon ?? (dataSource === 'sensortower' ? SensorTowerIcon : AppAnnie);
  const isMauMetric = sortMetric === 'mau' || key === 'mau';

  const getValueFormatter = (rawValue?: string | number) => {
    if (isMauMetric) {
      const formatted = valueFormat(rawValue);
      return formatted === '-' ? '-' : String(formatted);
    }
    const valueData = growthFormat({
      value: rawValue,
      valueType: 'percentValue',
      hasPoint: 'all',
      fixedNum: 2,
      maxNumber: 99999,
    });
    return valueData?.value || '-';
  };

  const width = 180;
  return {
    key: key,
    width: width,
    header: (
      <CloumsCenter width={width} align={'right'}>
        <SortBy
          title={displayTitle}
          defaultKey={currentSortKey}
          defaultValue={currentSortValue}
          svgIcon={headerSourceIcon}
          list={[
            {
              key: `${key}_0`,
              sortMetric: sortMetric,
              orderSource: dataSource,
              text: displayTitle,
              title: displayTitle,
              ratioType: 0,
            },
            {
              key: `${key}_1`,
              sortMetric: sortMetric,
              orderSource: dataSource,
              text: `${displayTitle}:${formatMessage({ id: 'dbi_change' })}`,
              title: title === 'Revenue($)' ? 'Revenue($)%' : `${displayTitle}(%)`,
              ratioType: 1,
            },
          ]}
          onChange={onSortChage}
          svgTip={
            <DataSourceBubbleInfo
              metricKey={metricKey || sortMetric}
              entityType={entityType}
              dataOfDom={`Retention ${title}`}
              comesFormDom={
                <>
                  <img src={getDataSourceIcon(dataSource)} />
                  {DATASOURCE_NAME[dataSource]}
                </>
              }
              dataSource={dataSource}
            />
          }
          content={textContent}
        />
      </CloumsCenter>
    ),
    align: 'center',
    render: (cvm: any) => {
      const metrics = cvm?.metric_list || [];
      const metric = metrics.find((item: any) => {
        return item?.metric === sortMetric;
      });
      const value = isMauMetric
        ? { value: getValueFormatter(metric?.value), type: '' }
        : growthFormat({
            value: metric?.value,
            valueType: 'percentValue',
            hasPoint: 'all',
            fixedNum: 2,
            maxNumber: 99999,
          });
      const growthDataObj = growthFormat({
        value: metric?.growth_ratio,
        valueType: 'pointValue',
        hasPoint: 'all',
        fixedNum: 2,
        maxNumber: 99999,
      });
      const dataCompareData = buildMetricCompareData({
        metric,
        entityType: entityType || 'mobile',
        title: cvm?.name,
        icon: cvm?.cover,
        granularity: dateType,
        startDate,
        endDate,
        prevValueConfig: {
          ratioValueKeys: ['growth_ratio'],
          diffValueKeys: ['dod'],
        },
        valueFormatter: getValueFormatter,
      });
      return (
        <CloumsCenter width={width} align={'right'}>
          <TableShowData
            maxNumber={isMauMetric ? '99999+' : '99999+%'}
            value={value?.value}
            type={growthDataObj?.type}
            growth={growthDataObj?.value}
            dataCompareData={dataCompareData}
          />
        </CloumsCenter>
      );
    },
  };
};

export const RetentionDetailMetricColumn = (params: ColumnType) => {
  const {
    key,
    title,
    sortMetric,
    dataSource,
    currentSortKey,
    currentSortValue,
    textContent,
    isNoDropdown,
    svgIcon,
    onSortChage,
    metricKey,
    entityType = '',
    gameName,
    gameCover,
    dateType,
    startDate,
    endDate,
    sourceDataOfDom,
  } = params;
  const width = 180;
  const { formatMessage } = useIntl();

  const getValueFormatter = (rawValue: string | number) => {
    const valueData = growthFormat({
      value: rawValue,
      valueType: 'percentValue',
      hasPoint: 'all',
      fixedNum: 2,
      maxNumber: 99999,
    });

    return valueData?.value || '-';
  };

  return {
    key: key,
    width: width,
    align: 'center',
    header: (
      <CloumsCenter width={width} align={'right'}>
        <SortBy
          title={title}
          defaultKey={currentSortKey}
          defaultValue={currentSortValue}
          svgIcon={svgIcon ?? AppAnnie}
          isNoDropdown={isNoDropdown}
          metricKey={metricKey || sortMetric}
          list={[
            {
              key: `${key}_0`,
              sortMetric: sortMetric,
              orderSource: dataSource,
              text: title,
              title: title,
              ratioType: 0,
            },
            {
              key: `${key}_1`,
              sortMetric: sortMetric,
              orderSource: dataSource,
              text: `${title}:${formatMessage({ id: 'dbi_change' })}(%)`,
              title: `${title}(%)`,
              ratioType: 1,
            },
          ]}
          onChange={onSortChage}
          svgTip={
            <DataSourceBubbleInfo
              dataOfDom={sourceDataOfDom || `Retention ${title}`}
              metricKey={metricKey}
              entityType={entityType}
              comesFormDom={
                <>
                  <img src={getDataSourceIcon(dataSource)} />
                  {DATASOURCE_NAME[dataSource]}
                </>
              }
              dataSource={dataSource}
            />
          }
          content={textContent}
        />
      </CloumsCenter>
    ),
    render: (cvm: any) => {
      const metrics = cvm?.metrics || [];
      const metric = metrics.find((item: any) => {
        return item?.name === key;
      });
      const value = growthFormat({
        value: metric?.value,
        valueType: 'percentValue',
        hasPoint: 'all',
        fixedNum: 2,
        maxNumber: 99999,
      });
      const growthDataObj = growthFormat({
        value: metric?.dod,
        valueType: 'pointValue',
        hasPoint: 'all',
        fixedNum: 2,
        maxNumber: 99999,
      });
      const dataCompareData = buildMetricCompareData({
        metric,
        entityType: entityType || 'mobile',
        title: cvm?.name || gameName,
        icon: cvm?.cover || gameCover,
        granularity: dateType,
        startDate,
        endDate,
        prevValueConfig: {
          ratioValueKeys: [],
          diffValueKeys: ['dod'],
        },
        valueFormatter: getValueFormatter,
      });
      return (
        <CloumsCenter width={width} align={'right'}>
          <TableShowData
            maxNumber={'99999+%'}
            value={value?.value}
            type={growthDataObj?.type}
            growth={growthDataObj?.value}
            dataCompareData={dataCompareData}
          />
        </CloumsCenter>
      );
    },
  };
};

export const RetentionLifetimeMetricColumn = (params: ColumnType) => {
  const {
    key,
    title,
    sortMetric,
    dataSource,
    currentSortKey,
    currentSortValue,
    textContent,
    isNoDropdown,
    onSortChage,
    metricKey,
    entityType=''
  } = params;
  const width = 120;

  return {
    key: key,
    width: width,
    align: 'center',
    header: (
      <CloumsCenter width={width} align={'right'}>
        <SortBy
          title={title}
          defaultKey={currentSortKey}
          defaultValue={currentSortValue}
          svgIcon={SensorTowerIcon}
          isNoDropdown={isNoDropdown}
          metricKey={metricKey}
          list={[
            {
              key: `${key}_0`,
              sortMetric: sortMetric,
              orderSource: dataSource,
              text: title,
              title: title,
              ratioType: 0,
            },
          ]}
          onChange={onSortChage}
          svgTip={
            <DataSourceBubbleInfo
              dataOfDom={`Retention ${title}`}
              metricKey={metricKey || sortMetric}
              entityType={entityType}
              comesFormDom={
                <>
                  <img src={getDataSourceIcon(dataSource)} />
                  {DATASOURCE_NAME[dataSource]}
                </>
              }
              dataSource={dataSource}
            />
          }
          content={textContent}
        />
      </CloumsCenter>
    ),
    render: (cvm: any) => {
      const metrics = cvm?.metrics || [];
      const metric = metrics.find((item: any) => {
        return item?.name === key;
      });
      const growthDataObj = growthFormat({
        value: metric?.value,
        valueType: 'percentValue',
        hasPoint: 'all',
        fixedNum: 2,
        maxNumber: 99999,
      });
      return (
        <CloumsCenter width={width} align={'right'}>
          {growthDataObj?.value}
        </CloumsCenter>
      );
    },
  };
};
/** Sensor Tower 月度留存表头 hover（TopCharts / 详情页共用文案） */
export const getRetentionMonthlySensortowerDxHoverContent = (
  formatMessage: (
    descriptor: { id: string },
    values?: Record<string, string>,
  ) => string,
  metricKey: string,
) => (
  <span style={{ whiteSpace: 'pre-line' }}>
    {formatMessage(
      { id: 'dbi_retention_monthly_sensortower_dx_hover' },
      {
        metric: metricKey,
        day: metricKey?.replace(/^D/i, '') || '',
      },
    )}
  </span>
);

const RetentionTip = (props: any) => {
  const { title } = props;
  const { formatMessage } = useIntl(); // 中英切换方法
  let tip: React.ReactNode = '';
  switch (title) {
    case 'mau':
      tip = formatMessage({ id: 'dbi_info_avg_selected' });
      break;
    case 'd2':
      tip = formatMessage({ id: 'dbi_info_retention_trend' }, { dn: '2nd' });
      break;
    case 'd3':
      tip = formatMessage({ id: 'dbi_info_retention_trend' }, { dn: '3rd' });
      break;
    case 'd7':
      tip = formatMessage({ id: 'dbi_info_retention_trend' }, { dn: '7th' });
      break;
    case 'd14':
      tip = formatMessage({ id: 'dbi_info_retention_trend' }, { dn: '14th' });
      break;
    case 'd15':
      // tip = (
      //   <>
      //     <p>
      //       {formatMessage({ id: 'dbi_info_retention_trend' }, { dn: '15th' })}
      //     </p>
      //     <p>{formatMessage({ id: 'dbi_info_retention_d15' })}</p>
      //   </>
      // );
      tip = (
        <>
          {formatMessage({ id: 'dbi_info_retention_trend' }, { dn: '15th' })}
          <br />
          {formatMessage({ id: 'dbi_info_retention_d15' })}
        </>
      );
      break;
    case 'd30':
      tip = formatMessage({ id: 'dbi_info_retention_trend' }, { dn: '30th' });
      break;
    case 'd31':
      // tip = (
      //   <>
      //     <p>
      //       {formatMessage(
      //         { id: 'dbi_info_retention_trend' },
      //         { dn: '31st' },
      //       )}
      //     </p>
      //     <p>{formatMessage({ id: 'dbi_info_retention_d31' })}</p>
      //   </>
      // );
      tip = (
        <>
          {formatMessage(
            { id: 'dbi_info_retention_trend' },
            { dn: '31st' },
          )}
          <br />
          {formatMessage({ id: 'dbi_info_retention_d31' })}
        </>
      );
      break;
    default:
      tip = '';
      break;
  }
  // return <>{tip}</>;
  return <>
    {tip}
    {!!tip && (
      <>
        <br />
        {formatMessage({ id: 'dbi_info_mobile_datasource_range' })}
      </>
    )}
  </>;
};

export const retentionOptions = [
  { title: 'MAU', key: 'mau', textContent: <RetentionTip title="mau" />, metricKey:'Retention', titleKey: 'dbi_mau' },
  { title: 'D2', key: 'D2', textContent: <RetentionTip title="d2" />,metricKey:'Retention' },
  { title: 'D3', key: 'D3', textContent: <RetentionTip title="d3" />,metricKey:'Retention' },
  { title: 'D7', key: 'D7', textContent: <RetentionTip title="d7" />,metricKey:'Retention' },
  { title: 'D15', key: 'D15', textContent: <RetentionTip title="d15" />,metricKey:'Retention' },
  { title: 'D31', key: 'D31', textContent: <RetentionTip title="d31" />,metricKey:'Retention' },
];

export const retentionAvgOptions = [
  { title: 'Avg D2', key: 'D2' },
  { title: 'Avg D3', key: 'D3' },
  { title: 'Avg D7', key: 'D7' },
  { title: 'Avg D15', key: 'D15' },
  { title: 'Avg D31', key: 'D31' },
];

export const retentionLifetimeOptions = [
  { title: 'D2', key: 'D2', textContent: <RetentionTip title="d2" />,metricKey:'Retention' },
  { title: 'D3', key: 'D3', textContent: <RetentionTip title="d3" />,metricKey:'Retention' },
  { title: 'D7', key: 'D7', textContent: <RetentionTip title="d7" />,metricKey:'Retention' },
  { title: 'D14', key: 'D14', textContent: <RetentionTip title="d14" />,metricKey:'Retention' },
  { title: 'D30', key: 'D30', textContent: <RetentionTip title="d30" />,metricKey:'Retention' },
];
